import { create } from 'zustand'

import {
  STOCK_REPORT_DEFAULT_LIMIT,
  STOCK_REPORT_DEFAULT_SORT_BY,
  STOCK_REPORT_DEFAULT_SORT_DIRECTION,
  STOCK_REPORT_DEFAULT_STATUS,
  STOCK_REPORT_MAX_LIMIT,
} from '@/features/reports/constants'
import type {
  StockReportSortField,
  StockReportStatusFilter,
} from '@/features/reports/types/stock-report.types'
import type { SortDirection } from '@/types/common.types'

import { getDefaultStockReportAsOf } from '@/features/reports/utils/stockReportDate'

interface StockReportViewState {
  page: number
  limit: number
  search: string
  category: string
  brand: string
  status: StockReportStatusFilter
  sortBy: StockReportSortField
  sortDirection: SortDirection
  asOf: string
}

interface StockReportState {
  view: StockReportViewState
}

interface StockReportActions {
  setPage: (page: number) => void
  setLimit: (limit: number) => void
  setSearch: (search: string) => void
  setCategory: (category: string) => void
  setBrand: (brand: string) => void
  setStatus: (status: StockReportStatusFilter) => void
  setSortBy: (sortBy: StockReportSortField) => void
  setSortDirection: (sortDirection: SortDirection) => void
  setAsOf: (asOf: string) => void
  resetFilters: () => void
}

const initialState: StockReportState = {
  view: {
    page: 1,
    limit: STOCK_REPORT_DEFAULT_LIMIT,
    search: '',
    category: '',
    brand: '',
    status: STOCK_REPORT_DEFAULT_STATUS,
    sortBy: STOCK_REPORT_DEFAULT_SORT_BY,
    sortDirection: STOCK_REPORT_DEFAULT_SORT_DIRECTION,
    asOf: getDefaultStockReportAsOf(),
  },
}

export const useStockReportStore = create<StockReportState & StockReportActions>()(
  (set) => ({
    ...initialState,

    setPage: (page) => {
      set((state) => ({
        view: { ...state.view, page: Math.max(1, page) },
      }))
    },

    setLimit: (limit) => {
      set((state) => ({
        view: { ...state.view, page: 1, limit: Math.min(Math.max(1, limit), STOCK_REPORT_MAX_LIMIT) },
      }))
    },

    setSearch: (search) => {
      set((state) => ({
        view: { ...state.view, page: 1, search },
      }))
    },

    setCategory: (category) => {
      set((state) => ({
        view: { ...state.view, page: 1, category },
      }))
    },

    setBrand: (brand) => {
      set((state) => ({
        view: { ...state.view, page: 1, brand },
      }))
    },

    setStatus: (status) => {
      set((state) => ({
        view: { ...state.view, page: 1, status },
      }))
    },

    setSortBy: (sortBy) => {
      set((state) => ({
        view: { ...state.view, page: 1, sortBy },
      }))
    },

    setSortDirection: (sortDirection) => {
      set((state) => ({
        view: { ...state.view, page: 1, sortDirection },
      }))
    },

    setAsOf: (asOf) => {
      set((state) => ({
        view: {
          ...state.view,
          page: 1,
          asOf: asOf || getDefaultStockReportAsOf(),
        },
      }))
    },

    resetFilters: () => {
      set(initialState)
    },
  }),
)

export type { StockReportViewState }
